import React, { useEffect, useMemo, useState } from 'react';
import api from '../utils/api';
import Icon from './Icon';
import { maskKraPin } from '../utils/kraNameMatch';

const normalizeId = (value) => {
  if (!value) return '';
  return typeof value.toString === 'function' ? value.toString() : String(value);
};

const roleLabel = (role) => {
  if (role === 'senior') return 'Senior Manager';
  if (role === 'associate') return 'Associate';
  return 'Unknown';
};

const renderKraSummary = (user) => {
  const email = user?.email || 'No email';
  const kraPin = String(user?.kraPin || '').trim();
  const kraTaxpayerName = String(user?.kraTaxpayerName || '').trim();

  if (kraPin) {
    return `${email} • PIN: ${maskKraPin(kraPin)} • ${kraTaxpayerName || 'Taxpayer name unavailable'}`;
  }

  if (user?.kraPinCheckedAt) {
    return `${email} • User not verified`;
  }

  return `${email} • Verification pending`;
};

function ManagementCompanyStaffModal({ isOpen, onClose, company }) {
  const [staff, setStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffError, setStaffError] = useState('');
  const [properties, setProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [role, setRole] = useState('senior');
  const [assignedProperties, setAssignedProperties] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [editingUserId, setEditingUserId] = useState('');

  const assignedPropertyIds = useMemo(
    () => new Set(assignedProperties.map((id) => normalizeId(id))),
    [assignedProperties]
  );

  useEffect(() => {
    if (!isOpen || !company?._id) return;
    const load = async () => {
      setStaffLoading(true);
      setPropertiesLoading(true);
      setStaffError('');
      try {
        const [staffRes, propertiesRes] = await Promise.all([
          api.get(`/management-company/${company._id}/staff`),
          api.get('/frp/properties')
        ]);
        const staffList = Array.isArray(staffRes.data?.staff) ? staffRes.data.staff : [];
        setStaff(staffList);
        const allProps = Array.isArray(propertiesRes.data?.properties)
          ? propertiesRes.data.properties
          : [];
        const companyProps = allProps.filter((prop) => {
          const propCompany = prop.managementCompany?._id || prop.managementCompany;
          return normalizeId(propCompany) === normalizeId(company._id);
        });
        setProperties(companyProps);
      } catch (err) {
        console.error('Failed to load staff or properties', err);
        setStaffError(err.response?.data?.error || 'Failed to load staff');
      } finally {
        setStaffLoading(false);
        setPropertiesLoading(false);
      }
    };
    load();
  }, [isOpen, company?._id]);

  const resetForm = () => {
    setSelectedUser(null);
    setRole('senior');
    setAssignedProperties([]);
    setEditingUserId('');
    setFormError('');
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;
    setSearchLoading(true);
    setSearchResults([]);
    setFormError('');
    try {
      const res = await api.get(`/auth/search-users?idNumber=${encodeURIComponent(searchTerm.trim())}`);
      const list = Array.isArray(res.data?.users) ? res.data.users : [];
      setSearchResults(list);
      if (list.length === 0) {
        setFormError(`No users found with ID "${searchTerm.trim()}"`);
      }
    } catch (err) {
      console.error('Search users failed', err);
      setFormError(err.response?.data?.error || 'Failed to search users');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchResults([]);
    setSearchTerm(user?.idNumber || '');
    setFormError('');
  };

  const handleToggleProperty = (propertyId) => {
    setAssignedProperties((prev) => {
      const exists = prev.some((id) => normalizeId(id) === normalizeId(propertyId));
      if (exists) {
        return prev.filter((id) => normalizeId(id) !== normalizeId(propertyId));
      }
      return [...prev, propertyId];
    });
  };

  const handleEditStaff = (entry) => {
    if (!entry?.user) return;
    setSelectedUser(entry.user);
    setSearchTerm(entry.user?.idNumber || '');
    setRole(entry.role || 'associate');
    const assigned = Array.isArray(entry.assignedProperties)
      ? entry.assignedProperties.map((p) => p?._id || p)
      : [];
    setAssignedProperties(assigned);
    setEditingUserId(entry.user?._id || '');
    setFormError('');
  };

  const handleRemoveStaff = async (entry) => {
    if (!entry?.user?._id) return;
    if (!window.confirm(`Remove ${entry.user?.name || 'this user'} from staff?`)) return;
    try {
      await api.delete(`/management-company/${company._id}/staff/${entry.user._id}`);
      setStaff((prev) => prev.filter((s) => normalizeId(s.user?._id || s.user) !== normalizeId(entry.user._id)));
      if (normalizeId(editingUserId) === normalizeId(entry.user._id)) {
        resetForm();
      }
    } catch (err) {
      console.error('Remove staff failed', err);
      setFormError(err.response?.data?.error || 'Failed to remove staff');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser?._id) {
      setFormError('Select a user first.');
      return;
    }
    if (!role) {
      setFormError('Select a role.');
      return;
    }
    if (role === 'associate' && assignedProperties.length === 0) {
      setFormError('Associates must have at least one assigned property.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const payload = {
        role,
        assignedProperties: role === 'associate' ? assignedProperties : []
      };
      if (editingUserId) {
        await api.put(`/management-company/${company._id}/staff/${editingUserId}`, payload);
      } else {
        await api.post(`/management-company/${company._id}/staff`, {
          userId: selectedUser._id,
          ...payload
        });
      }
      const staffRes = await api.get(`/management-company/${company._id}/staff`);
      setStaff(Array.isArray(staffRes.data?.staff) ? staffRes.data.staff : []);
      resetForm();
      setSearchTerm('');
    } catch (err) {
      console.error('Save staff failed', err);
      setFormError(err.response?.data?.error || 'Failed to save staff');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !company) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl sm:rounded-2xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-4 sm:p-6 flex items-center justify-between flex-shrink-0">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg sm:text-xl font-bold text-white truncate">Manage Staff</h2>
            <p className="text-xs sm:text-sm text-green-100 truncate">{company.companyName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors flex-shrink-0 ml-2"
          >
            <Icon name="x" size="1.5rem" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6 sm:space-y-8">
          {staffError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-red-700 text-sm">{staffError}</p>
            </div>
          )}

          {/* Current Staff Section */}
          <section className="space-y-3 sm:space-y-4">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Current Staff</h3>
              {!staffLoading && staff.length > 0 && (
                <span className="ml-auto bg-green-100 text-green-700 text-xs font-medium px-2 py-1 rounded-full">
                  {staff.length} {staff.length === 1 ? 'member' : 'members'}
                </span>
              )}
            </div>

            {staffLoading ? (
              <div className="flex items-center justify-center py-8 text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                <span className="ml-3 text-sm">Loading staff...</span>
              </div>
            ) : staff.length === 0 ? (
              <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl p-8 text-center">
                <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p className="text-sm text-gray-500">No staff assigned yet</p>
                <p className="text-xs text-gray-400 mt-1">Add your first team member below</p>
              </div>
            ) : (
              <div className="space-y-2 sm:space-y-3">
                {staff.map((entry) => (
                  <div
                    key={entry._id || entry.user?._id}
                    className="bg-gradient-to-br from-gray-50 to-green-50/30 border border-gray-200 rounded-lg sm:rounded-xl p-3 sm:p-4 hover:shadow-md transition-all duration-200"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2 mb-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-gray-900 text-sm sm:text-base truncate">
                              {entry.user?.name || 'Unknown User'}
                            </div>
                            <div className="text-xs text-gray-500 truncate">{renderKraSummary(entry.user)}</div>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${entry.role === 'senior'
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-blue-100 text-blue-700'
                            }`}>
                            {roleLabel(entry.role)}
                          </span>
                        </div>
                        {entry.role === 'associate' && (
                          <div className="mt-2 p-2 bg-white/60 rounded-lg border border-gray-200">
                            <div className="text-xs font-medium text-gray-600 mb-1">Assigned Properties:</div>
                            <div className="text-xs text-gray-700">
                              {Array.isArray(entry.assignedProperties) && entry.assignedProperties.length > 0
                                ? entry.assignedProperties.map((p) => p?.name || 'Unknown').join(', ')
                                : 'None assigned'}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 sm:flex-col sm:gap-2">
                        <button
                          onClick={() => handleEditStaff(entry)}
                          className="flex-1 sm:flex-none px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors shadow-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleRemoveStaff(entry)}
                          className="flex-1 sm:flex-none px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-medium hover:bg-red-700 transition-colors shadow-sm"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Add/Edit Staff Section */}
          <section className="space-y-4 bg-white border border-gray-200 rounded-xl p-4 sm:p-6">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                {editingUserId ? 'Update Staff Member' : 'Add Staff Member'}
              </h3>
            </div>

            {formError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-red-700 text-sm">{formError}</p>
              </div>
            )}

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Search by ID Number
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="flex-1 p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  placeholder="Enter ID number"
                />
                <button
                  onClick={handleSearch}
                  disabled={searchLoading || !searchTerm.trim()}
                  className="px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm shadow-sm"
                >
                  {searchLoading ? (
                    <span className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Searching...
                    </span>
                  ) : 'Search'}
                </button>
              </div>
              {searchResults.length > 0 && (
                <div className="border border-gray-200 rounded-lg divide-y max-h-48 overflow-y-auto">
                  {searchResults.map((u) => (
                    <button
                      key={u._id}
                      type="button"
                      onClick={() => handleSelectUser(u)}
                      className="w-full text-left p-3 hover:bg-green-50 transition-colors"
                    >
                      <div className="text-sm font-medium text-gray-900">{u.name}</div>
                      <div className="text-xs text-gray-500">{renderKraSummary(u)}</div>
                    </button>
                  ))}
                </div>
              )}
              {selectedUser && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="text-xs text-green-700">
                    <span className="font-medium">Selected:</span> {selectedUser.name} ({renderKraSummary(selectedUser)})
                  </div>
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                >
                  <option value="senior">Senior Manager</option>
                  <option value="associate">Associate</option>
                </select>
                <p className="text-xs text-gray-500 mt-1.5">
                  {role === 'senior'
                    ? 'Can manage all properties and see everything like the company admin'
                    : 'Can only view assigned properties'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assigned Properties
                  {role === 'associate' && <span className="text-red-500 ml-1">*</span>}
                </label>
                {propertiesLoading ? (
                  <div className="flex items-center justify-center py-4 text-gray-500">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                    <span className="ml-2 text-sm">Loading properties...</span>
                  </div>
                ) : properties.length === 0 ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center text-sm text-gray-500">
                    No properties found for this company
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {properties.map((prop) => (
                        <label
                          key={prop._id}
                          className={`flex items-center gap-2 p-2 rounded-lg text-sm transition-colors ${role === 'associate'
                              ? 'hover:bg-gray-50 cursor-pointer'
                              : 'opacity-50 cursor-not-allowed'
                            }`}
                        >
                          <input
                            type="checkbox"
                            disabled={role !== 'associate'}
                            checked={assignedPropertyIds.has(normalizeId(prop._id))}
                            onChange={() => handleToggleProperty(prop._id)}
                            className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                          />
                          <span className={role === 'associate' ? 'text-gray-700' : 'text-gray-400'}>
                            {prop.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {role !== 'associate' && (
                  <p className="text-xs text-gray-500 mt-1.5">
                    Senior managers have access to all properties automatically
                  </p>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                {editingUserId && (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="flex-1 py-2.5 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium text-sm"
                  >
                    Cancel Edit
                  </button>
                )}
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-2.5 px-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium text-sm shadow-sm"
                >
                  {saving ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Saving...
                    </span>
                  ) : editingUserId ? 'Update Staff Member' : 'Add Staff Member'}
                </button>
              </div>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
}

export default ManagementCompanyStaffModal;
